import { useFeatureValue } from '@growthbook/growthbook-react';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { useEffect, useMemo } from 'react';
import { runtimeEnv } from '../config/runtimeEnv';
import { FEATURE_FLAG } from '../constants/featureFlags';
import { useMarketplaceConfig } from '../queries/marketplace';
import {
  identifyOpenReplayUser,
  resetOpenReplayUser,
  startOpenReplay,
  stopOpenReplay,
  type OpenReplayFeatureConfig,
} from '../utils/openReplay';

const DEFAULT_OPENREPLAY_FEATURE_CONFIG: OpenReplayFeatureConfig = {
  enabled: false,
  captureConsole: false,
  captureErrors: false,
  captureNetwork: false,
};

const OpenReplayProvider = () => {
  const featureConfig = useFeatureValue<OpenReplayFeatureConfig>(
    FEATURE_FLAG.OPENREPLAY_TRACKING,
    DEFAULT_OPENREPLAY_FEATURE_CONFIG
  );
  const { isAuthenticated, preferredUsername, userId } = useAuth();
  const { data: marketplaceConfig } = useMarketplaceConfig();
  const openReplayProjectKey = marketplaceConfig?.marketplaceConfig?.openReplayProjectKey;

  const metadata = useMemo(
    () => ({
      appVersion: runtimeEnv.version,
      brand: marketplaceConfig?.name,
      environment: runtimeEnv.environment,
      tenantId: marketplaceConfig?.id,
      userId: userId ?? '',
      username: preferredUsername,
    }),
    [marketplaceConfig?.id, marketplaceConfig?.name, preferredUsername, userId]
  );

  useEffect(() => {
    startOpenReplay({ featureConfig, runtimeEnv, projectKey: openReplayProjectKey });

    const handlePageHide = () => {
      stopOpenReplay();
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handlePageHide);
      stopOpenReplay();
    };
  }, [featureConfig, openReplayProjectKey]);

  useEffect(() => {
    if (!isAuthenticated || !metadata.userId) {
      resetOpenReplayUser();
      return;
    }

    identifyOpenReplayUser(metadata);
  }, [isAuthenticated, metadata]);

  return null;
};

export default OpenReplayProvider;
